import { useState } from 'react';
import { Box, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { type GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ClienteSelect } from '../../../shared/components/ClienteSelect';
import { ErrorState } from '../../../shared/components/ErrorState';
import { describeApiError } from '../../../shared/api/errors';
import { projectsApi } from '../api';
import type { ProjectListItem } from '../types';
import { ProjectColaboradoresDialog } from '../ProjectColaboradoresDialog';
import { ProjectRequisicoesDialog } from '../../clientes/projects/ProjectRequisicoesDialog';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'nome',
  sortOrder: 'asc',
};

/** Saldo RC é valor — formata em BRL (pós-adapter vem number; List cru manda "0.00"). */
function formatMoney(raw: number | string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  const n = Number(String(raw).replace(',', '.'));
  if (!Number.isFinite(n)) return String(raw);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Total/Qtd. RC é CONTAGEM de RCs vinculadas — inteiro (pós-adapter number). */
function formatCount(raw: number | string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '0';
  const n = Number(String(raw).replace(',', '.'));
  return Number.isFinite(n) ? String(Math.round(n)) : String(raw);
}

export function ProjetosGlobalPage(): JSX.Element {
  const { t } = useTranslation();
  const {
    query,
    setQuery,
    filters,
    patchFilters,
    clear,
  } = usePersistedGridState('projetos-global', DEFAULT_QUERY, {
    clienteCodigo: null as number | null,
  });
  const clienteFiltro = filters.clienteCodigo;
  const setClienteFiltro = (id: number | null) => patchFilters({ clienteCodigo: id });
  const [colabFor, setColabFor] = useState<ProjectListItem | null>(null);
  const [rcFor, setRcFor] = useState<ProjectListItem | null>(null);

  const listQuery = useQuery({
    queryKey: ['projects', 'global', query, clienteFiltro],
    queryFn: () => projectsApi.list(query, { clienteId: clienteFiltro }),
    placeholderData: (previous) => previous,
  });

  const columns: GridColDef<ProjectListItem>[] = [
    {
      field: 'clientNome',
      headerName: t('features.clientes.projetosGlobal.columns.cliente'),
      flex: 1,
      minWidth: 200,
    },
    { field: 'sigla', headerName: t('features.clientes.projetosGlobal.columns.sigla'), width: 120 },
    {
      field: 'nome',
      headerName: t('features.clientes.projetosGlobal.columns.nome'),
      flex: 1,
      minWidth: 220,
    },
    {
      field: 'totalRequisicaoCompra',
      headerName: t('features.clientes.projetosGlobal.columns.totalRC'),
      width: 150,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_, row) => formatCount(row.totalRequisicaoCompra),
    },
    {
      field: 'totalColaboradores',
      headerName: t('features.clientes.projetosGlobal.columns.qtdColaboradores'),
      width: 160,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_, row) => formatCount(row.totalColaboradores),
    },
    {
      field: 'saldoRequisicaoCompra',
      headerName: t('features.clientes.projetosGlobal.columns.saldoRC'),
      width: 150,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_, row) => formatMoney(row.saldoRequisicaoCompra),
    },
    {
      field: 'ativo',
      headerName: t('features.clientes.projetosGlobal.columns.ativo'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={
            params.value
              ? t('features.clientes.projetos.status.active')
              : t('features.clientes.projetos.status.inactive')
          }
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('features.clientes.projetosGlobal.colaboradores.tooltip')}>
            <IconButton
              size="small"
              aria-label={t('features.clientes.projetosGlobal.colaboradores.aria', {
                nome: params.row.nome,
              })}
              onClick={() => setColabFor(params.row)}
            >
              <GroupIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('features.clientes.projetosGlobal.requisicoes.tooltip')}>
            <IconButton
              size="small"
              aria-label={t('features.clientes.projetosGlobal.requisicoes.aria', {
                nome: params.row.nome,
              })}
              onClick={() => setRcFor(params.row)}
            >
              <RequestQuoteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Box>
      <PageHeader
        title={t('features.clientes.projetosGlobal.title')}
        subtitle={t('features.clientes.projetosGlobal.subtitle')}
      />
      <Stack spacing={2}>
        <DataTable<ProjectListItem>
          rows={listQuery.data?.data ?? []}
          columns={columns}
          total={listQuery.data?.meta.total ?? 0}
          loading={listQuery.isLoading || listQuery.isFetching}
          query={query}
          onQueryChange={setQuery}
          onClear={clear}
          searchPlaceholder={t('features.clientes.projetosGlobal.searchPlaceholder')}
          filters={
            <Box sx={{ minWidth: 220, flex: '1 1 260px' }}>
              <ClienteSelect
                value={clienteFiltro}
                onChange={(id) => setClienteFiltro(id)}
                label={t('features.clientes.projetosGlobal.filters.cliente')}
                placeholder={t('features.clientes.projetosGlobal.filters.clienteTodos')}
              />
            </Box>
          }
        />
      </Stack>

      {colabFor && (
        <ProjectColaboradoresDialog
          open={colabFor !== null}
          clientId={colabFor.clientId}
          projectId={colabFor.id}
          projectNome={colabFor.nome}
          onClose={() => setColabFor(null)}
        />
      )}

      {rcFor && (
        <ProjectRequisicoesDialog
          open={rcFor !== null}
          clientId={rcFor.clientId}
          projectId={rcFor.id}
          projectNome={rcFor.nome}
          onClose={() => setRcFor(null)}
        />
      )}
    </Box>
  );
}
